import { useState, type JSX } from 'react';
import { authApi } from '../api/client.js';
import { useI18n } from '../i18n/context.js';

/**
 * Signing in before the email is confirmed is allowed, generating is not
 * (01-SPEC.md §5.2, §7). The banner says why part of the app is closed instead of
 * letting it fail silently, and offers the one thing to do about it.
 */
export function EmailVerificationBanner({ email }: { readonly email: string }): JSX.Element {
  const { t } = useI18n();
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle');

  return (
    <div className="px-4 pt-5">
      <div
        role="alert"
        className="mx-auto flex max-w-7xl flex-col items-center gap-4 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-center sm:flex-row sm:text-left"
      >
        <span
          aria-hidden="true"
          className="grid size-11 shrink-0 place-items-center rounded-full bg-amber-100 text-amber-700"
        >
          <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor">
            <rect x="3" y="5" width="18" height="14" rx="2.5" strokeWidth="1.8" />
            <path d="m4 7 8 6 8-6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-amber-950">{t.auth.verify.bannerTitle}</p>
          <p className="mt-0.5 text-sm text-amber-900">{t.auth.verify.bannerText(email)}</p>
        </div>
        {state === 'sent' ? (
          <p role="status" className="shrink-0 text-sm font-medium text-emerald-800">
            ✓ {t.auth.verify.resent}
          </p>
        ) : (
          <button
            type="button"
            className="kitup-secondary-button shrink-0"
            disabled={state === 'sending'}
            onClick={() => {
              setState('sending');
              void authApi.resendVerification().finally(() => setState('sent'));
            }}
          >
            {t.auth.verify.resend}
          </button>
        )}
      </div>
    </div>
  );
}
